import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { MarketingConsent } from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Grid,
  Typography,
  useDesktopMediaQuery,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useCheckout } from '@jane/shared-ecomm/providers';

import { StyledGridContainer } from '../checkout.styles';
import { CheckoutAccordionItem } from '../CheckoutAccordion/CheckoutAccordionItem';
import { CheckoutStoreCard } from '../CheckoutStoreCard/CheckoutStoreCard';
import { useFulfillmentSchedule } from '../util/hooks/useFulfillmentSchedule';
import { useReservationModeLabels } from '../util/hooks/useReservationModeLabels';
import { resolveFulfillmentSlot } from '../util/resolveFulfillmentSlot';
import { AgreementLabel } from './AgreementLabel';
import { AgreementLinks } from './AgreementLinks';
import { PickupSummary } from './PickupSummary';
import { useCheckoutOptionsForm } from './useCheckoutOptionsForm';

export const CheckoutPickupOptions = () => {
  const isDesktop = useDesktopMediaQuery({ size: 'md', width: 'min' });
  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;

  const { consentItems, consentsValid, handleContinue, isValid } =
    useCheckoutOptionsForm();
  const {
    appInfo: { appMode, bloomUserExperience },
    cart: { reservation_mode },
    store: { curbside_pickup_instructions, pickup_message_enabled },
  } = useCheckout();
  const { curbsideLabel, pickupLabel } = useReservationModeLabels();
  const {
    curbsideSlots,
    pickupSlots,
    isLoadingPickup,
    isAcceptingCurbside,
    isAcceptingPickup,
  } = useFulfillmentSchedule();

  const { control, trigger, getValues, setValue } = useFormContext();

  const formPickupMode = useWatch({
    name: 'pickupOptions.pickupMode',
    control,
  });

  const shouldValidate = reservation_mode === 'pickup';

  const fulfillmentSlots =
    formPickupMode === 'pickup' ? pickupSlots : curbsideSlots;

  const handleChangeMode = (mode: 'pickup' | 'curbside') => {
    const targetSlots = mode === 'pickup' ? pickupSlots : curbsideSlots;
    setValue(
      'pickupOptions.fulfillmentTime',
      resolveFulfillmentSlot(
        getValues('pickupOptions.fulfillmentTime'),
        targetSlots
      )
    );
  };

  const pickupMethods = [
    ...(isAcceptingPickup
      ? [
          {
            id: 'pickup',
            label: pickupLabel,
            value: 'pickup',
          },
        ]
      : []),
    ...(isAcceptingCurbside
      ? [
          {
            id: 'curbside',
            label: curbsideLabel,
            value: 'curbside',
          },
        ]
      : []),
  ];

  const slotOptions =
    fulfillmentSlots?.map((slot) => ({
      label: slot.display_name,
      value: `${slot.from}>${slot.to}`,
    })) || [];

  useEffect(() => {
    // When switching to pickup, we re-validate delivery fields
    // as they are no longer required
    trigger('deliveryOptions');
  }, []);

  return (
    <CheckoutAccordionItem
      id="options"
      header="Confirm your reservation details"
      summary={<PickupSummary />}
      mt={bloomUserExperience && !isDesktop ? 16 : 0}
    >
      <Box>
        <Typography variant="body-bold" mb={12}>
          Store
        </Typography>
        <CheckoutStoreCard />

        <Box my={24}>
          {/* Right cell: pickup-method radio (flag off) or curbside instructions
              (flag on). When neither, the time selector expands to full width. */}
          {(() => {
            const showRadio = !curbsideFlagOn;
            const showCurbsideInstructions =
              curbsideFlagOn &&
              !!curbside_pickup_instructions &&
              formPickupMode === 'curbside';
            const hasRightCell = showRadio || showCurbsideInstructions;

            const gridChildren = [
              <Grid.Item key="pickup-time" xs={12} lg={hasRightCell ? 6 : 12}>
                <Typography variant="body-bold" mb={12}>
                  Pickup time
                </Typography>
                <Form.SelectField
                  borderRadius="xs"
                  disableMobileInputStyling
                  label="Pickup time"
                  name="pickupOptions.fulfillmentTime"
                  loading={isLoadingPickup}
                  options={slotOptions}
                  placeholder={
                    slotOptions.length
                      ? 'Select a reservation slot'
                      : 'No reservation times available'
                  }
                  validate={(value) =>
                    !shouldValidate || !!value || 'Please select a pickup time'
                  }
                  shouldUnregister={false}
                  labelHidden
                />
              </Grid.Item>,
              showRadio && (
                <Grid.Item key="pickup-method" xs={12} lg={6}>
                  <Typography variant="body-bold" mb={12}>
                    Pickup method
                  </Typography>

                  <Form.RadioFieldGroup
                    row={isDesktop}
                    name="pickupOptions.pickupMode"
                    options={pickupMethods}
                    onChange={handleChangeMode}
                    shouldUnregister={false}
                    mb={-24}
                  />
                </Grid.Item>
              ),
              showCurbsideInstructions && (
                <Grid.Item key="curbside-instructions" xs={12} lg={6}>
                  <Typography mb={12} variant="body-bold">
                    Curbside instructions
                  </Typography>
                  <Typography>{curbside_pickup_instructions}</Typography>
                </Grid.Item>
              ),
            ].filter((node): node is ReactElement => Boolean(node));

            return (
              <StyledGridContainer spacing={24}>
                {gridChildren}
              </StyledGridContainer>
            );
          })()}
        </Box>

        {!curbsideFlagOn &&
          curbside_pickup_instructions &&
          formPickupMode === 'curbside' && (
            <Box mb={24}>
              <Typography mb={12} variant="body-bold">
                Curbside instructions
              </Typography>
              <Typography>{curbside_pickup_instructions}</Typography>
            </Box>
          )}

        {pickup_message_enabled && (
          <>
            <Typography variant="body-bold" mb={12}>
              Pickup notes
            </Typography>
            <Form.TextField
              my={24}
              borderRadius="xs"
              disableMobileInputStyling
              label="Pickup notes"
              name="pickupOptions.message"
              shouldUnregister={false}
              maxLength={300}
              labelHidden
            />
          </>
        )}

        {!!consentItems.length && (
          <Box mb={24}>
            {consentItems.map((item) => (
              <Box key={item.id} mb={24}>
                <Form.CheckboxField
                  mt={12}
                  label={
                    <AgreementLabel
                      required={item.required}
                      label={item.label}
                    />
                  }
                  name={`pickupOptions.${item.name}`}
                  aria-describedby={
                    item.external_link || item.external_links.length > 0
                      ? `agreement-links-${item.id}`
                      : undefined
                  }
                  validate={(value) =>
                    !shouldValidate ||
                    !!value ||
                    !item.required ||
                    'This is a required agreement.'
                  }
                  shouldUnregister={false}
                />
                {(item.external_link || item.external_links.length > 0) && (
                  <AgreementLinks
                    id={`agreement-links-${item.id}`}
                    agreementLabel={item.label}
                    external_link={item.external_link}
                    external_links={item.external_links}
                  />
                )}
              </Box>
            ))}

            {!consentsValid && (
              <Typography color="error" mt={12}>
                Please agree to the required terms.
              </Typography>
            )}

            {appMode === 'default' && (
              <Flex mt={12} flexDirection="row">
                <Form.CheckboxField
                  disableMobileInputStyling
                  name="pickupOptions.promotionsOptIn"
                  label="Promotional emails"
                  labelHidden
                  defaultChecked
                />
                <MarketingConsent />
              </Flex>
            )}
          </Box>
        )}

        <Button
          disabled={!isValid}
          onClick={() => handleContinue()}
          full
          label="Continue"
        />
      </Box>
    </CheckoutAccordionItem>
  );
};
